<script lang="ts" setup>
import moment from 'moment';
import { onMounted, ref } from 'vue';
import './Polizas.scss';
import PolicyRepository from '../../repositories/policies.repository';
import {
  ApiPolicyCompanyOption,
  ApiPolicyFilters as ApiPolicyFilterValues,
  IPolicie,
} from '../../types/policies.type';
import storeAuth from '@store/auth';
import { isAuditUser } from '../../helpers/auditUser';
import { formatterPrice } from '../../helpers/formatterPrice';
import { describeResponseMips } from '../../helpers/responseMips';
import { useI18n } from 'vue-i18n'
import ApiPolicyFilters from '../../components/Polizas/ApiPolicyFilters.vue';
import DataTable from '@components/DataTable.vue';
import { useToast } from '../../composables/useToast';

const isLoadingPolicies = ref<boolean>(false);
const isDownloadingExcel = ref<boolean>(false);

const { t } = useI18n();
const { $toast } = useToast();
const { user } = storeAuth();
const isAudit = isAuditUser(user?.email);
const policyRepository = new PolicyRepository();
const policiesData = ref<IPolicie[]>([]);
const paginationData = ref();
const isOpenDialog = ref<boolean>(false);
const idDeleteItem = ref<number | null>(null);
const currentPage = ref<number>(1);
const appliedFilters = ref<ApiPolicyFilterValues>({});
const companies: ApiPolicyCompanyOption[] = [
  { label: 'Vink', value: 'Vink' },
  { label: 'ControlMatch', value: 'ControlMatch' },
  { label: 'Logytek', value: 'Logytek' },
  { label: 'Abmuss', value: 'Abmuss' },
];

const fieldsTable: any = ref([
  {
    key: 'id',
    label: t('polizas.polizas.id'),
    sortable: true,
  },
  {
    key: 'policyNumber',
    label: t('polizas.polizas.policyNumber'),
  },
  {
    key: 'insuredName',
    label: t('global.asegurado'),
    sortable: true,
  },
  {
    key: 'asset',
    label: t('polizas.polizas.id_de_carga'),
    formatter: (e: any) => e?.id || null,
  },
  {
    key: 'policyType',
    label: t('polizas.polizas.tipo_de_carga'),
  },
  {
    key: 'asset',
    label: t('polizas.polizas.descripcion_de_la_carga'),
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'customCatalogIds',
    label: t('polizas.polizas.ids_del_catalogo'),
  },
  {
    key: 'customCatalog',
    label: t('polizas.polizas.descripcion_del_catalogo'),
  },
  {
    key: 'createdAt',
    label: t('polizas.polizas.fecha_de_creacion'),
    formatter: (e: any) => moment(e).format('DD-MM-YYYY HH:mm'),
  },
  {
    key: 'status',
    label: t('global.estado'),
    formatter: (e: any) => (e == 'in_progress' ? 'En progreso' : 'Cancelada'),
  },
  {
    key: 'indemnificationLimit',
    label: t('polizas.polizas.suma_asegurada'),
    formatter: (e: any) => (e ? formatterPrice(e) : null),
  },
  {
    key: 'forceMonitorMIPS',
    label: t('polizas.polizas.fuerza_monitoreo'),
	formatter: (e: any) => (e == 0 ? 'NO' : e ? 'SI' : null),
  },
  {
    key: 'responseMips',
    label: t('polizas.polizas.mips'),
    formatter: (e: any) => describeResponseMips(e),
  },
  {
    key: 'actions',
    label: t('global.acciones'),
  },
]);

const isAbmussPolicy = (item: any) => item?.apiKey?.appName === 'Abmuss';

const handleDownloadCertificate = async ({ id }: any) => {
  await policyRepository.downloadCertificatePdf(id);
};

const handleDelete = ({ id }: any) => {
  isOpenDialog.value = true;
  idDeleteItem.value = id;
};

const getPolicyFilters = (page: number = 1) => ({
  ...appliedFilters.value,
  page,
});

const loadPolicies = async (page: number = 1) => {
  isLoadingPolicies.value = true;
  try {
    const { policies } = await policyRepository.getPolicies(getPolicyFilters(page));
    policiesData.value = policies?.policies ?? [];
    paginationData.value = policies?.pagination;
    currentPage.value = page;
  } finally {
    isLoadingPolicies.value = false;
  }
};

const handleConfirmDelete = async () => {
  if (idDeleteItem.value !== null) {
    try {
      await policyRepository.deletePolicy(idDeleteItem.value);
    } catch (error: any) {
      $toast?.error(error?.message ?? t('global.ocurrio_un_error'));
      isOpenDialog.value = false;
      return;
    }
  }
  await loadPolicies(currentPage.value);
  isOpenDialog.value = false;
};

const onChangePage = async (page: number) => {
  await loadPolicies(page);
};

const downloadExcel = async (filters: ApiPolicyFilterValues) => {
  isDownloadingExcel.value = true;
  try {
    await policyRepository.getExcelPolicies({
      search: filters.search,
      clientId: filters.clientId,
      certificateNumber: filters.certificateNumber,
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      company: filters.company ?? null,
      toExcel: true,
    });
  } finally {
    isDownloadingExcel.value = false;
  }
};

const handleOnSubmit = async (filters: ApiPolicyFilterValues) => {
  appliedFilters.value = filters;
  await loadPolicies(1);
};

const handleClearFilters = async () => {
  appliedFilters.value = {};
  await loadPolicies(1);
};

onMounted(() => {
  loadPolicies(1);
});

const getRowTable = (policiesData: any, type: string) => {
  const catalogue = policiesData?.data?.item?.catalogue;

  if (!catalogue) {
    return null;
  }

  const concatenatedString = catalogue
    .map((item: any) => {
      switch (type) {
        case 'descriptions':
          return [item.description, item.otherDescription]
            .filter(Boolean)
            .join(', ');
        case 'ids':
          return [item.id].filter(Boolean).join(', ');
        default:
          return '';
      }
    })
    .join(', ');

  return concatenatedString || null;
};

</script>
<template>
  <Layout :title="t('polizas.polizas.listar_polizas_terrestres_api')" :textColor="'color-secondary'">
    <template #layout>
      <div class="poliza">
        <ApiPolicyFilters
          type-policy-transport="Terrestre"
          :companies="companies"
          :show-company="!user?.apiKeyId"
          :show-download="!isAudit"
          :is-downloading-excel="isDownloadingExcel"
          @search="handleOnSubmit"
          @reset="handleClearFilters"
          @download="downloadExcel"
        />
        <DataTable
          :loading="isLoadingPolicies"
          :fields="fieldsTable"
          :items="policiesData"
          @update:model-value-page="onChangePage"
          :totalRows="paginationData?.count"
          :perPage="20"
          :currentPage="paginationData?.current_page"
          :hover="true"
        >
          <template v-if="true" v-slot:cell(actions)="policiesData">
            <div class="d-flex align-items-center gap-2">
              <img
                v-if="isAbmussPolicy(policiesData.data.item)"
                src="/icons/pdf.svg"
                style="width: 32px; cursor: pointer"
                :title="t('global.certificado')"
                @click="handleDownloadCertificate(policiesData.data.item)"
              />
              <button
                v-if="!isAudit"
                class="background-pink p-1 border-0 flex-grow-1 text-white rounded-4 uppercase"
                @click="handleDelete(policiesData.data.item)"
              >
			  {{ t('global.cancelar') }}
              </button>
            </div>
          </template>
          <template v-if="true" v-slot:cell(customCatalog)="policiesData">
            <div class="d-flex align-self-center">
              <p
                class="truncatedText"
                :title="getRowTable(policiesData, 'descriptions')"
              >
                {{ getRowTable(policiesData, 'descriptions') }}
              </p>
            </div>
          </template>
          <template v-if="true" v-slot:cell(customCatalogIds)="policiesData">
            <div class="d-flex align-self-center">
              <p
                class="truncatedText"
                :title="getRowTable(policiesData, 'ids')"
              >
                {{ getRowTable(policiesData, 'ids') }}
              </p>
            </div>
          </template>
        </DataTable>
        <Dialog
          :centered="true"
          :visible="isOpenDialog"
          @cancel="isOpenDialog = false"
          @confirm="handleConfirmDelete"
        />
      </div>
    </template>
  </Layout>
</template>

<style scoped>
.truncatedText {
  max-width: 15rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
  margin: 0;
}
</style>
